import { useEffect } from 'react';
import { batch, useSelector, useDispatch } from 'react-redux';
import { useImmer } from 'use-immer';
import axios from 'axios';
import { Cluster, Vector as VectorSource } from 'ol/source';
import { Icon, Style, Text, Fill } from 'ol/style';
import { Vector as VectorLayer } from 'ol/layer';
import GeoJSON from 'ol/format/GeoJSON';
import * as extent from 'ol/extent';

import marker from '../../../images/marker.svg';
import marker_cluster from '../../../images/marker_cluster.svg';

const clusterIconStyle = new Style({
  image: new Icon({
    size:[64,64],
    anchor: [32, 32],
    anchorXUnits: 'pixels',
    anchorYUnits: 'pixels',
    src: marker_cluster,
  }),
  text: new Text({
      font: `bold 11px Cerebri Sans`,
      fill: new Fill({
          color: '#fff'
      })
  })
});

const iconStyle = new Style({
  image: new Icon({
    size:[48,48],
    anchor: [24, 36],
    anchorXUnits: 'pixels',
    anchorYUnits: 'pixels',
    src: marker,
  }),
  text: new Text({
      offsetY: -13,
      font: `bold 9px Cerebri Sans`,
      fill: new Fill({
          color: '#fff'
      })
  })
});

function _getLayer(map,id){
  var l = !1,
  d = map.getLayers().getArray(),
  i = d.length - 1;
  while(i>=0){
    if(d[i].get('name') === id){
      l = d[i];
      break;
    }
    i--;
  }
  return l;
}

let vectorLayer = !1;
let clusterLayer = !1;

const Lmarkers = () => {
  const setApp = useDispatch();
  const { map } = useSelector(s => s.map);
  const { allFeatures, markers } = useSelector(s => s.markers);
  const [styleCache_cluster, setStyleCache_cluster] = useImmer({})

  var zIndex = 0;

  useEffect(()=>{
    if (!markers.length) {
      axios.get('/markers?t='+new Date().getTime()).then(r => {
        setApp({type:'_MARKERS_INIT',payload:r.data})

        if (window.studyId) {
          setApp({
            type: '_STUDY_DETAIL',
            payload: ['studyID', [window.studyId]]
          });
        }
      }).catch(() => {
        axios.get('/markers?t='+new Date().getTime())
        .then((r)=>{
          setApp({type:'_MARKERS_INIT',payload:r.data})
        })
      });
    }

    return () => {}
  }, []);

  useEffect(() => {
    if (map && markers.length) {
      const cl = _getLayer(map, 'cluster-markers');
      const ml = _getLayer(map, 'markers');
      if (cl) {
        map.removeLayer(cl);
        map.removeLayer(ml);
      }

      const features = new GeoJSON({
        featureProjection: 'EPSG:3857'
      }).readFeatures({
        "type": "FeatureCollection",
        "features": markers
      })

      const vectorSource = new VectorSource({
        features: features
      });

      const clusterSource = new Cluster({
        distance: 55,
        source: vectorSource,
        wrapX: false
      });

      clusterLayer = new VectorLayer({
        source: clusterSource,
        maxZoom: 5.9999999999999999999999999999999999999,
        style: function (f) {
          var size = 0;
          f.get('features').map((d) => {
            var t = d.get('studyId');
            return size = size + t.length
          })
          if (!styleCache_cluster[size]) {
            clusterIconStyle.getText().setText(`${size}`);
            setStyleCache_cluster({ [size]: clusterIconStyle });
          } else {
            clusterIconStyle.getText().setText(`${styleCache_cluster[size]}`);
          }

          return clusterIconStyle;
        },
        name: 'cluster-markers'
      });

      vectorLayer = new VectorLayer({
        source: vectorSource,
        minZoom: 6,
        style: function (f) {
          const arr = f.getProperties().studyId
          const size = arr.length

          if (f.getProperties().n) {
            iconStyle.getText().setText(`${size > 1 ? size : ''}`);
            iconStyle.setZIndex(zIndex++);
          } else {
            iconStyle.getText().setText(`${size > 1 ? size : ''}`);
            iconStyle.setZIndex(zIndex++);
          }

          return iconStyle;
        },
        name: 'markers'
      });

      map.addLayer(clusterLayer);
      map.addLayer(vectorLayer);

      if (!allFeatures) {
        batch(()=>{
          setApp({
            type: '_MARKERS',
            payload: [
              'markersExtent',
              {
                center:extent.getCenter(vectorSource.getExtent()),
                zoom:map.getView().getZoomForResolution(
                  map.getView().getResolutionForExtent(vectorSource.getExtent())
                ) -0.25
              }
            ]
          });
        });
      }

      setApp({
        type: '_MARKERS',
        payload: [
          'filteredExtent',
          {
            center: extent.getCenter(vectorSource.getExtent()),
            zoom: map.getView().getZoomForResolution(
              map.getView().getResolutionForExtent(vectorSource.getExtent())
            ) -0.25
          }
        ]
      });

      if (vectorSource) {
        const t = markers.length;
        map.getView().animate({
          center: extent.getCenter(vectorSource.getExtent()),
          zoom: t > 1
            ? (
                map.getView().getZoomForResolution(
                  map.getView().getResolutionForExtent(vectorSource.getExtent())
                ) -0.25
              )
            : 12,
          duration: t > 1 ? 1500 : 2000
        });
      }
    }

    return () => {
      if (clusterLayer) {
        map.removeLayer(clusterLayer);
      }

      if (vectorLayer) {
        map.removeLayer(vectorLayer);
      }
    }
  }, [markers]);

  return null;
}

export default Lmarkers;
